import { useState } from 'react'
import { CheckCircle2, ChevronDown, ChevronRight, XCircle, AlertCircle, UploadCloud } from 'lucide-react'
import { clsx } from 'clsx'
import { Link } from 'react-router-dom'
import { useChecklist, type ChecklistSection } from '../../hooks/useChecklist'

function CompletionBar({ pct }: { pct: number }) {
  const color =
    pct === 100 ? 'bg-emerald-500' :
    pct > 0     ? 'bg-amber-400'   :
                  'bg-red-500/60'
  return (
    <div className="h-1.5 w-24 rounded-full bg-white/[0.06] overflow-hidden shrink-0">
      <div className={clsx('h-full rounded-full transition-all duration-500', color)} style={{ width: `${pct}%` }} />
    </div>
  )
}

function StatusIcon({ ok, required }: { ok: boolean; required: boolean }) {
  if (ok) return <CheckCircle2 size={14} className="text-emerald-400 shrink-0" />
  if (required) return <XCircle size={14} className="text-red-400 shrink-0" />
  return <AlertCircle size={14} className="text-[--km-text-disabled] shrink-0" />
}

function SectionRow({ sec }: { sec: ChecklistSection }) {
  const [open, setOpen] = useState(sec.completeness_pct < 100)

  const statusColor =
    sec.completeness_pct === 100 ? 'text-emerald-400' :
    sec.completeness_pct > 0     ? 'text-amber-400'   :
                                    'text-red-400'

  return (
    <div className="border border-white/[0.06] rounded-xl overflow-hidden">
      {/* Header riga sezione */}
      <button
        className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-white/[0.03] transition-colors text-left"
        onClick={() => setOpen(o => !o)}
      >
        {open
          ? <ChevronDown size={14} className="text-[--km-text-muted] shrink-0" />
          : <ChevronRight size={14} className="text-[--km-text-muted] shrink-0" />
        }
        <span className="flex-1 text-sm font-semibold text-[--km-text-primary]">{sec.label}</span>
        <CompletionBar pct={sec.completeness_pct} />
        <span className={clsx('text-xs font-bold tabular-nums w-8 text-right', statusColor)}>
          {sec.completeness_pct.toFixed(0)}%
        </span>
      </button>

      {/* Dettaglio tabelle */}
      {open && (
        <div className="border-t border-white/[0.06] px-4 py-3 space-y-2.5 bg-white/[0.01]">
          {sec.tables.map(t => (
            <div key={t.table} className="flex items-start gap-2.5">
              <StatusIcon ok={t.has_data} required={t.required} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={clsx(
                    'text-sm',
                    t.has_data ? 'text-[--km-text-primary]' : 'text-[--km-text-secondary]',
                  )}>
                    {t.label}
                  </span>
                  {t.required && !t.has_data && (
                    <span className="text-[10px] font-bold uppercase tracking-wide text-red-400 bg-red-500/10 rounded-full px-2 py-0.5">
                      Richiesto
                    </span>
                  )}
                  {!t.required && (
                    <span className="text-[10px] font-medium text-[--km-text-disabled] bg-white/[0.04] rounded-full px-2 py-0.5">
                      Opzionale
                    </span>
                  )}
                  {t.has_data && (
                    <span className="text-[10px] text-[--km-text-muted] tabular-nums">
                      {t.row_count.toLocaleString('it-IT')} righe
                    </span>
                  )}
                </div>
                {!t.has_data && (
                  <p className="text-xs text-[--km-text-muted] mt-0.5 leading-relaxed">{t.file_hint}</p>
                )}
              </div>
            </div>
          ))}

          {/* CTA upload se mancano dati */}
          {sec.missing_hints.length > 0 && (
            <div className="pt-1">
              <Link
                to="/upload"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-400 hover:text-indigo-300 transition-colors"
              >
                <UploadCloud size={12} />
                Carica i file mancanti
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export function DataCoverageChecklist() {
  const { data: sections, isLoading } = useChecklist()

  if (isLoading) {
    return (
      <div className="km-panel p-5">
        <p className="text-sm text-[--km-text-muted]">Analisi completezza dati...</p>
      </div>
    )
  }

  if (!sections) return null

  const complete = sections.filter(s => s.completeness_pct === 100).length
  const total = sections.length
  const overallPct = Math.round((complete / total) * 100)

  return (
    <div className="km-panel p-5 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-bold text-[--km-text-primary] uppercase tracking-wider">
            Completezza dati per sezione
          </h2>
          <p className="text-xs text-[--km-text-muted] mt-0.5">
            {complete} su {total} sezioni con dati completi
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-2 w-32 rounded-full bg-white/[0.06] overflow-hidden">
            <div
              className={clsx(
                'h-full rounded-full transition-all duration-700',
                overallPct === 100 ? 'bg-emerald-500' : overallPct > 50 ? 'bg-amber-400' : 'bg-red-500/70',
              )}
              style={{ width: `${overallPct}%` }}
            />
          </div>
          <span className="text-sm font-bold tabular-nums text-[--km-text-secondary]">{overallPct}%</span>
        </div>
      </div>

      {/* Lista sezioni */}
      <div className="space-y-2">
        {sections.map(sec => (
          <SectionRow key={sec.section} sec={sec} />
        ))}
      </div>

      {/* Legenda */}
      <div className="flex items-center gap-4 pt-1 border-t border-white/[0.05]">
        <div className="flex items-center gap-1.5 text-[11px] text-[--km-text-muted]">
          <CheckCircle2 size={11} className="text-emerald-400" /> Dati presenti
        </div>
        <div className="flex items-center gap-1.5 text-[11px] text-[--km-text-muted]">
          <XCircle size={11} className="text-red-400" /> Richiesto — mancante
        </div>
        <div className="flex items-center gap-1.5 text-[11px] text-[--km-text-muted]">
          <AlertCircle size={11} className="text-[--km-text-disabled]" /> Opzionale — mancante
        </div>
      </div>
    </div>
  )
}
